import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { useAuth } from '@/context/AuthContext';
import { useLeagueConfig } from '@/hooks/useLeagueConfig';
import { useTeamCount } from '@/hooks/useTeamCount';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LeagueSettingsLayout } from '@/components/LeagueSettingsLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Save, Loader2, LogOut, Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

export default function LeagueSettings() {
  const { user, currentMembership, setCurrentMembership, isSuperAdmin, refreshMemberships } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [league, setLeague] = useState<{ id: string; name: string; slug: string; season: string; settings: Record<string, unknown> } | null>(null);
  const [name, setName] = useState('');
  const [showFormIndicators, setShowFormIndicators] = useState(true);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const { config: leagueConfig, competition, isLoading: configLoading, showFormIndicators: configShowForm } = useLeagueConfig();
  const teamCount = useTeamCount(competition.id, league?.season ?? undefined);

  useEffect(() => {
    if (!configLoading) setShowFormIndicators(configShowForm);
  }, [configLoading, configShowForm]);

  const canEdit =
    isSuperAdmin ||
    currentMembership?.role === 'owner' ||
    currentMembership?.role === 'admin';

  const isOwner = currentMembership?.role === 'owner' || isSuperAdmin;

  useEffect(() => {
    if (currentMembership?.league_id) {
      loadLeague();
    } else {
      const timeout = setTimeout(() => {
        if (!currentMembership?.league_id) setLoading(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership?.league_id]);

  const loadLeague = async () => {
    if (!currentMembership?.league_id) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('leagues').select('*').eq('id', currentMembership.league_id).single();
      if (error || !data) { toast({ title: 'Error loading league', variant: 'destructive' }); setLoading(false); return; }
      setLeague({ id: data.id, name: data.name, slug: data.slug, season: data.season, settings: (data.settings as Record<string, unknown>) || {} });
      setName(data.name);
    } catch (e) { console.error('Exception loading league:', e); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!league || !canEdit) return;
    setSaving(true);
    const { error: leagueError } = await supabase.from('leagues').update({ name }).eq('id', league.id);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    let { error: configError } = await sb
      .from('league_configs')
      .update({ show_form_indicators: showFormIndicators })
      .eq('league_id', league.id);
    if (!configError) {
      // If no row existed, insert one
      const { count } = await sb.from('league_configs').select('league_id', { count: 'exact', head: true }).eq('league_id', league.id);
      if (count === 0) {
        ({ error: configError } = await sb.from('league_configs').insert({ league_id: league.id, show_form_indicators: showFormIndicators }));
      }
    }
    const error = leagueError || configError;
    if (error) { toast({ title: 'Failed to save', description: error.message, variant: 'destructive' }); }
    else {
      toast({ title: 'Settings saved' });
      setLeague((prev) => prev ? { ...prev, name } : prev);
      refreshMemberships();
      queryClient.invalidateQueries({ queryKey: ['league-config'] });
    }
    setSaving(false);
  };

  const handleLeaveLeague = async () => {
    if (!currentMembership || !user) return;
    if (currentMembership.role === 'owner') {
      toast({ title: "Can't leave as owner", description: 'Transfer ownership to another member before leaving.', variant: 'destructive' });
      return;
    }
    setActionLoading(true);
    const { error } = await supabase.from('league_memberships').update({ status: 'left' }).eq('id', currentMembership.id);
    if (error) { toast({ title: 'Failed to leave league', description: error.message, variant: 'destructive' }); }
    else {
      toast({ title: 'You have left the league' });
      setCurrentMembership(null);
      localStorage.removeItem('socrates-current-membership');
      localStorage.removeItem('socrates-super-admin-membership');
      await refreshMemberships();
      navigate('/dashboard');
    }
    setActionLoading(false);
    setLeaveDialogOpen(false);
  };

  const handleDeleteLeague = async () => {
    if (!league || !isOwner) return;
    setActionLoading(true);
    try {
      const { error } = await invokeEdgeFunction('delete-league', { body: { leagueId: league.id, reason: deleteReason } });
      if (error) { toast({ title: 'Failed to delete league', description: error.message, variant: 'destructive' }); setActionLoading(false); setDeleteDialogOpen(false); return; }
      toast({ title: 'League deleted' });
      setCurrentMembership(null);
      localStorage.removeItem('socrates-current-membership');
      localStorage.removeItem('socrates-super-admin-membership');
      await refreshMemberships();
      navigate('/dashboard');
    } catch (e) { console.error('Exception deleting league:', e); toast({ title: 'Failed to delete league', description: 'An unexpected error occurred', variant: 'destructive' }); }
    setActionLoading(false);
    setDeleteDialogOpen(false);
  };

  if (!currentMembership) {
    return (
      <LeagueSettingsLayout activeTab="general">
        <p className="text-stone-600">No league selected</p>
      </LeagueSettingsLayout>
    );
  }

  if (loading) {
    return (
      <LeagueSettingsLayout activeTab="general">
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </div>
      </LeagueSettingsLayout>
    );
  }

  return (
    <LeagueSettingsLayout activeTab="general">
      <Card>
        <CardHeader>
          <CardTitle>League Details</CardTitle>
          <CardDescription>
            {canEdit ? 'Update your league information' : 'View league information'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">League Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} />
          </div>
          <div className="space-y-2">
            <Label>Season</Label>
            <Input value={league?.season || ''} disabled />
          </div>
          <div className="space-y-2">
            <Label>Competition</Label>
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border">
              <span className="text-2xl">{competition.flag}</span>
              <div>
                <p className="font-medium">{competition.name}</p>
                <p className="text-xs text-stone-500">
                  {competition.country} &middot; {competition.matchdays} matchdays &middot; {teamCount} teams
                </p>
              </div>
            </div>
          </div>
          {!configLoading && (
            <div className="space-y-2">
              <Label>Game Rules</Label>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-stone-50 rounded border">
                  <span className="text-stone-500">Max picks per team</span>
                  <p className="font-medium">{leagueConfig.team_pick_limit}x per season</p>
                </div>
                <div className="p-2 bg-stone-50 rounded border">
                  <span className="text-stone-500">TDD limit</span>
                  <p className="font-medium">{leagueConfig.tdd_limit} per season</p>
                </div>
                <div className="p-2 bg-stone-50 rounded border">
                  <span className="text-stone-500">TDD ineligible</span>
                  <p className="font-medium">Top {leagueConfig.tdd_ineligible_count}</p>
                </div>
                <div className="p-2 bg-stone-50 rounded border">
                  <span className="text-stone-500">Deadline buffer</span>
                  <p className="font-medium">{leagueConfig.deadline_buffer_minutes} min</p>
                </div>
              </div>
            </div>
          )}
          {!configLoading && canEdit && (
            <div className="space-y-2">
              <Label>Display Options</Label>
              <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border">
                <div>
                  <p className="text-sm font-medium">Performance Indicators</p>
                  <p className="text-xs text-stone-500">Show streak arrows and Socrates icon in standings</p>
                </div>
                <Switch
                  checked={showFormIndicators}
                  onCheckedChange={setShowFormIndicators}
                />
              </div>
            </div>
          )}
          {canEdit && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          )}
        </CardContent>
      </Card>

      {(isOwner || (!isSuperAdmin && currentMembership.role !== 'owner')) && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {isOwner && (
            <Button
              variant="ghost"
              size="sm"
              className="text-stone-400 hover:text-stone-600 text-xs"
              onClick={() => { setDeleteReason(''); setDeleteDialogOpen(true); }}
            >
              <Trash2 className="h-3 w-3 mr-1.5" />
              Delete League
            </Button>
          )}
          {!isSuperAdmin && currentMembership.role !== 'owner' && (
            <Button
              variant="ghost"
              size="sm"
              className="text-stone-400 hover:text-stone-600 text-xs"
              onClick={() => setLeaveDialogOpen(true)}
            >
              <LogOut className="h-3 w-3 mr-1.5" />
              Leave League
            </Button>
          )}
        </div>
      )}

      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave {league?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave this league? Your predictions and history will be preserved, but you'll need a new invite to rejoin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveLeague} disabled={actionLoading} className="bg-red-600 hover:bg-red-700">
              {actionLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Leaving...</> : 'Leave League'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {league?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the league and all associated data including predictions, selections, standings, and member history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-sm font-medium mb-2 block">Why are you deleting this league?</Label>
            <Select value={deleteReason} onValueChange={setDeleteReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="season_ended">Season ended</SelectItem>
                <SelectItem value="not_enough_members">Not enough members</SelectItem>
                <SelectItem value="switching_competition">Switching to a different competition</SelectItem>
                <SelectItem value="created_by_mistake">Created by mistake</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteLeague} disabled={actionLoading || !deleteReason}>
              {actionLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : 'Delete League'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </LeagueSettingsLayout>
  );
}
